import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { baseURL } from '../../Services/api/baseURL';

export const inventoryApi = createApi({
    reducerPath: 'inventoryApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${baseURL}/api/admin/inventory`,
        prepareHeaders: (headers) => {
            const rawToken = localStorage.getItem('token');
            if (rawToken) {
                try {
                    const token = JSON.parse(rawToken);
                    headers.set('authorization', token);
                } catch (e) {
                    headers.set('authorization', rawToken);
                }
            }
            return headers;
        },

    }),
    tagTypes: [
        'Supplier',
        'Store',
        'PurchaseOrder',
        'StockReceipt',
        'Adjustment',
        'Return',

        'Stocktake',
        'InventorySnapshot',
        'Movement',
    ],
    endpoints: (builder) => ({
        // Suppliers
        getSuppliers: builder.query({
            query: (params) => ({
                url: '/supplier',
                params,
            }),
            providesTags: ['Supplier'],
        }),
        getSupplierById: builder.query({
            query: (id) => `/supplier/${id}`,
            providesTags: (result, error, id) => [{ type: 'Supplier', id }],
        }),
        createSupplier: builder.mutation({
            query: (body) => ({
                url: '/supplier',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Supplier'],
        }),
        updateSupplier: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `/supplier/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Supplier', id }, 'Supplier'],
        }),


        // Stores
        getStores: builder.query({
            query: () => '../../store/get-all-stores',
            providesTags: ['Store'],
        }),
        getStoreById: builder.query({
            query: (id) => `../../store/get-store/${id}`,
            providesTags: (result, error, id) => [{ type: 'Store', id }],
        }),
        createStore: builder.mutation({
            query: (body) => ({
                url: '../../store/add-store',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Store'],
        }),
        updateStore: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `../../store/update-store/${id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Store', id }, 'Store'],
        }),
        getStoreInventory: builder.query({
            query: (storeId) => `/store/${storeId}/inventory`,
            providesTags: (result, error, storeId) => [{ type: 'InventorySnapshot', id: storeId }],
        }),

        // Purchase Orders
        getPurchaseOrders: builder.query({
            query: (params) => ({
                url: '/po',
                params,
            }),
            providesTags: ['PurchaseOrder'],
        }),
        getPurchaseOrderById: builder.query({
            query: (id) => `/po/${id}`,
            providesTags: (result, error, id) => [{ type: 'PurchaseOrder', id }],
        }),
        createPurchaseOrder: builder.mutation({
            query: (body) => ({
                url: '/po',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PurchaseOrder'],
        }),
        updatePurchaseOrder: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `/po/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'PurchaseOrder', id }, 'PurchaseOrder'],
        }),
        approvePurchaseOrder: builder.mutation({
            query: (id) => ({
                url: `/po/${id}/approve`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, id) => [{ type: 'PurchaseOrder', id }, 'PurchaseOrder'],
        }),
        markPurchaseOrderOrdered: builder.mutation({
            query: (id) => ({
                url: `/po/${id}/order`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, id) => [{ type: 'PurchaseOrder', id }, 'PurchaseOrder'],
        }),

        // Returns to Supplier
        getReturnsSupplier: builder.query({
            query: (params) => ({
                url: '/returns/supplier',
                params,
            }),
            providesTags: ['Return'],
        }),
        getReturnSupplierById: builder.query({
            query: (id) => `/returns/supplier/${id}`,
            providesTags: (result, error, id) => [{ type: 'Return', id }],
        }),
        createReturnSupplier: builder.mutation({
            query: (body) => ({
                url: '/returns/supplier',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Return'],
        }),
        updateReturnSupplier: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/returns/supplier/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { id }) => ['Return', { type: 'Return', id }],
        }),
        dispatchReturn: builder.mutation({
            query: (id) => ({
                url: `/returns/supplier/${id}/dispatch`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, id) => ['Return', { type: 'Return', id }, 'InventorySnapshot', 'Movement'],
        }),

        // Movements (Activity Log)
        getMovements: builder.query({
            query: (params) => ({
                url: '/reports/movements',
                params,
            }),
            providesTags: ['Movement'],
        }),


        receiveStock: builder.mutation({
            query: (body) => ({
                url: '/receive',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['StockReceipt', 'InventorySnapshot', 'PurchaseOrder', 'Movement'],
        }),



        // Stocktakes
        getStocktakes: builder.query({
            query: (params) => ({
                url: '/stocktakes',
                params,
            }),
            providesTags: ['Stocktake'],
        }),
        getStocktakeById: builder.query({
            query: (id) => `/stocktakes/${id}`,
            providesTags: (result, error, id) => [{ type: 'Stocktake', id }],
        }),
        createStocktake: builder.mutation({
            query: (body) => ({
                url: '/stocktakes',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Stocktake'],
        }),
        updateStocktake: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/stocktakes/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { id }) => ['Stocktake', { type: 'Stocktake', id }],
        }),
        completeStocktake: builder.mutation({
            query: (id) => ({
                url: `/stocktakes/${id}/complete`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, id) => ['Stocktake', { type: 'Stocktake', id }, 'InventorySnapshot', 'Movement'],
        }),
        verifyStocktake: builder.mutation({
            query: (id) => ({
                url: `/stocktakes/${id}/verify`,
                method: 'POST',
            }),
        }),

        // Bulk Operations
        bulkPreview: builder.mutation({
            query: (body) => ({
                url: '/bulk/preview',
                method: 'POST',
                body,
            }),
        }),
        bulkApply: builder.mutation({
            query: (body) => ({
                url: '/bulk/apply',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['InventorySnapshot', 'Movement'],
        }),

        // Adjustments
        adjustStock: builder.mutation({
            query: (body) => ({
                url: '/adjust',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['InventorySnapshot', 'Movement'],
        }),

        // Audit & Movement Log
        getMovementLog: builder.query({
            query: (params) => ({
                url: '/movements',
                params,
            }),
            providesTags: ['Movement'],
        }),

        // Reporting
        getInventorySnapshot: builder.query({
            query: (params) => ({
                url: '/reports/stock-snapshot',
                params,
            }),
            providesTags: ['InventorySnapshot'],
        }),
        getLowStockReport: builder.query({
            query: (params) => ({
                url: '/reports/low-stock',
                params,
            }),
            providesTags: ['InventorySnapshot'],
        }),
        getMovementReport: builder.query({
            query: (params) => ({
                url: '/reports/movements',
                params,
            }),
            providesTags: ['Movement'],
        }),
        getAdjustments: builder.query({
            query: (params) => ({
                url: '/adjustments',
                params,
            }),
            providesTags: ['Adjustment'],
        }),
        getBulkHistory: builder.query({
            query: (params) => ({
                url: '/bulk/history',
                params,
            }),
            providesTags: ['Bulk'],
        }),
    }),
});

export const {
    useGetSuppliersQuery,
    useGetSupplierByIdQuery,
    useCreateSupplierMutation,
    useUpdateSupplierMutation,
    useGetPurchaseOrdersQuery,
    useGetMovementsQuery,
    useGetStoresQuery,
    useGetStoreByIdQuery,
    useCreateStoreMutation,
    useUpdateStoreMutation,
    useGetStoreInventoryQuery,
    useGetPurchaseOrderByIdQuery,
    useCreatePurchaseOrderMutation,
    useUpdatePurchaseOrderMutation,
    useApprovePurchaseOrderMutation,
    useMarkPurchaseOrderOrderedMutation,

    useReceiveStockMutation,

    useGetReturnsSupplierQuery,
    useGetReturnSupplierByIdQuery,
    useCreateReturnSupplierMutation,
    useUpdateReturnSupplierMutation,
    useDispatchReturnMutation,
    useGetStocktakesQuery,
    useGetStocktakeByIdQuery,
    useCreateStocktakeMutation,
    useUpdateStocktakeMutation,
    useCompleteStocktakeMutation,
    useVerifyStocktakeMutation,
    useBulkPreviewMutation,
    useBulkApplyMutation,
    useAdjustStockMutation,
    useGetMovementLogQuery,
    useGetInventorySnapshotQuery,
    useGetLowStockReportQuery,
    useGetMovementReportQuery,
    useGetAdjustmentsQuery,
    useGetBulkHistoryQuery,
} = inventoryApi;
